import DriveSidebar from 'components/layout/DriveSidebar'
import Page from 'rui/widgets/Page'
import { Spacer } from 'roadie-ui'
import { useDispatch, useSelector } from 'react-redux'
import { Breadcrumb } from 'components/misc/Breadcrumb'
import React from 'react'
import { DrivingProfile } from 'types/profile'
import { fetchDriverScorecardSummary } from 'actions/scorecard'
import FAQSection, { FAQItem } from '../FAQSection'
import RateMeterSection, { RateMessages } from '../RateMeterSection'
import OnTimeRateRangesModal from './OnTimeRateRangesModal'
import { ON_TIME_RATE_METRIC_NAME, getScorecardByMetricName, Scorecard } from 'util/scorecard'
import { FeatureFlag } from 'Optimizely'
import { Redirect } from 'react-router-dom'
import OnTimeRateCalculationSection from './OnTimeRateCalculationSection'
import OnTimeRateTipsSection from './OnTimeRateTipsSection'
import { useLocalization, type TranslationKey } from 'localization'

type RateMessageKeys = Record<keyof RateMessages, { titleKey: TranslationKey; bodyKey: TranslationKey }>
type FAQItemKeys = { titleKey: TranslationKey; bodyKey: TranslationKey }[]

const MESSAGE_KEYS: RateMessageKeys = {
  green: {
    titleKey: 'dweb_dashboard_on_time_rate_rate_message_green_title',
    bodyKey: 'dweb_dashboard_on_time_rate_rate_message_green_body',
  },
  yellow: {
    titleKey: 'dweb_common_almost_there',
    bodyKey: 'dweb_dashboard_on_time_rate_rate_message_yellow_body',
  },
  red: {
    titleKey: 'dweb_dashboard_on_time_rate_rate_message_red_title',
    bodyKey: 'dweb_dashboard_on_time_rate_rate_message_red_body',
  },
}

const ON_TIME_RATE_FAQ_KEYS: FAQItemKeys = [
  {
    titleKey: 'dweb_dashboard_on_time_rate_faq_why_important_title',
    bodyKey: 'dweb_dashboard_on_time_rate_faq_why_important_body',
  },
  {
    titleKey: 'dweb_dashboard_on_time_rate_faq_affect_acceptance_title',
    bodyKey: 'dweb_dashboard_on_time_rate_faq_affect_acceptance_body',
  },
  {
    titleKey: 'dweb_dashboard_on_time_rate_faq_know_if_improves_title',
    bodyKey: 'dweb_dashboard_on_time_rate_faq_know_if_improves_body',
  },
]

const OnTimeRateContent: React.FC = () => {
  const { t } = useLocalization()
  const profile = useSelector<any, DrivingProfile>((state) => state.profile)
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const scorecardSummary = useSelector((state: { scorecardSummary: Scorecard[] }) => state.scorecardSummary)
  const onTimeRateScorecard = getScorecardByMetricName(scorecardSummary, ON_TIME_RATE_METRIC_NAME)

  const dispatch = useDispatch()
  React.useEffect(() => {
    fetchDriverScorecardSummary(profile.id)(dispatch)
  }, [dispatch, profile.id])

  if (!onTimeRateScorecard) {
    return null
  }

  const { score } = onTimeRateScorecard
  const wholeNumberRate = Math.floor(score)
  const messages: RateMessages = {
    green: { title: t(MESSAGE_KEYS.green.titleKey), body: t(MESSAGE_KEYS.green.bodyKey) },
    yellow: { title: t(MESSAGE_KEYS.yellow.titleKey), body: t(MESSAGE_KEYS.yellow.bodyKey) },
    red: { title: t(MESSAGE_KEYS.red.titleKey), body: t(MESSAGE_KEYS.red.bodyKey) },
  }
  const faqs: FAQItem[] = ON_TIME_RATE_FAQ_KEYS.map(({ titleKey, bodyKey }) => ({
    title: t(titleKey),
    body: t(bodyKey),
  }))

  return (
    <Page sidebar={<DriveSidebar />}>
      {!isWebView && (
        <Breadcrumb
          items={[
            { label: t('dweb_common_my_profile'), linkTo: '/dashboard' },
            { label: t('dweb_common_on_time_rate') },
          ]}
        />
      )}
      <RateMeterSection
        score={wholeNumberRate}
        description={t('dweb_dashboard_on_time_rate_based_on_last_100_completed_gigs')}
        messages={messages}
        yellowMin={85}
        greenMin={90}
        RangesModal={OnTimeRateRangesModal}
      />
      <Spacer $height="2.4rem" />
      <OnTimeRateCalculationSection />
      <Spacer $height="2.4rem" />
      <OnTimeRateTipsSection />
      <Spacer $height="2.4rem" />
      <FAQSection items={faqs} />
    </Page>
  )
}

const OnTimeRatePage: React.FC = () => (
  <FeatureFlag feature="on_time_rate_scorecard">
    {(enabled) => (enabled ? <OnTimeRateContent /> : <Redirect to="/dashboard" />)}
  </FeatureFlag>
)

export default OnTimeRatePage
